import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import classes from './tneModals.module.scss';
import MainButton from '../../buttons/MainButton';
import { tneAxiosClient } from '../../../../api/apiSettings';
import OutlinedCheckBox from '../../Form/OutlinedCheckBox/OutlinedCheckBox';
import { PageContext } from '../../../../context/context';
import { generalEnums, submissionEnum } from '../../../../utils/enums';
import colors from '../../../../scss/colors';
import CustomMonthPicker from '../../CustomMonthPicker/CustomMonthPicker';
import { APPROVE_ALL_EXPENSES_COUNT, calendarTypes, responsiveSizeTimesheet } from '../../../../utils/constants';
import CustomCalendarPicker from '../../CustomWeekPicker/CustomCalendarPicker';
import {
  basicErrorHandler,
  buildMonth,
  getAllDaysInMonth,
  getFirstDayOFMonth,
  getBiMonthlyDays
} from '../../../../utils/helpers';
import TimeandExpensePopup from './TimeandExpensePopup/TimeandExpensePopup';
import SubmitModal from './SubmitModal/SubmitModal';
import { useWindowSize } from '../../../../utils/hooks';
import CustomBiMonthlyPicker from '../../CustomBiMonthlyPicker/CustomBiMonthlyPicker';
import { buttonVariants } from '../../buttons/types';
import CustomBiWeeklyPicker from '../../CustomBiWeeklyPicker/CustomBiWeeklyPicker';
import { Attachments } from '../../../pages/timesheet';

const shortDays = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];

function UnsavedChanges({ saveHandler, closeHandler, rejectPreventing }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const [canClose, setCanClose] = useState(false);
  const ctx = useContext(PageContext);

  useEffect(() => {
    if (canClose) {
      if (ctx.pageType === generalEnums.TIMESHEET) {
        navigate('/dashboard/timesheets');
      } else {
        navigate('/dashboard/expenses');
      }
    }
  }, [canClose]);

  return (
    <div className={`${classes.popupContainer} ${width > responsiveSizeTimesheet ? classes.withChangedWidth : ''}`}>
      <p className={`${classes.popupLabel} ${width > responsiveSizeTimesheet ? classes.withCorrectWidth : ''}`}>
        {t('dashboard.timesheet.unsavedmessage')}
      </p>
      <div className={classes.actionContainer} style={{ width: '100%' }}>
        <MainButton
          rounded
          outlined
          forceWidth={width > responsiveSizeTimesheet ? '164px' : '100%'}
          action={() => {
            rejectPreventing(false);
            setCanClose(true);
          }}
          variant={buttonVariants.SECONDARY}
        >
          {t('common.general.discard')}
        </MainButton>
        <MainButton
          rounded
          forceWidth={width > responsiveSizeTimesheet ? '164px' : '100%'}
          action={() => {
            saveHandler();
            closeHandler();
          }}
          variant={buttonVariants.PRIMARY}
        >
          {t('common.general.save')}
        </MainButton>
      </div>
    </div>
  );
}

function DeletePopUp({ deleteHandler, closeHandler, submissionType = null, withBackbtn = true, pending = false }) {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>
        {submissionType === submissionEnum.MONTHLY
          ? t('dashboard.timesheet.monthlydeletemessage')
          : t('dashboard.timesheet.deletemessage')}
      </p>
      <MainButton
        variant={buttonVariants.PRIMARY}
        rounded
        forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
        action={deleteHandler}
      >
        {pending ? <CircularProgress size={20} sx={{ color: colors.WHITE }} /> : t('common.general.delete')}
      </MainButton>
      {withBackbtn && (
        <MainButton
          rounded
          outlined
          variant={buttonVariants.SECONDARY}
          forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
          action={closeHandler}
        >
          {t('common.general.back')}
        </MainButton>
      )}
    </div>
  );
}

function PlacementModal({ message }) {
  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{message}</p>
    </div>
  );
}

function ApproveAllFinalModal({ message, closeHandler, refresh }) {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{message}</p>
      <MainButton
        rounded
        outlined
        forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
        action={() => {
          refresh();
          setSearchParams({ status: 'Pending' });
          closeHandler();
        }}
        variant={buttonVariants.PRIMARY}
      >
        {t('dashboard.timesheet.gotodashboard')}
      </MainButton>
    </div>
  );
}

function FinalModal({ message, isTimesheet = true, queryParams }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const [id, setId] = useState(null);

  const getNextDashboardId = async () => {
    try {
      const response = await tneAxiosClient.get(
        `v1/get-${isTimesheet ? 'timesheets' : 'expenses'}-by-approverId/?userId=${localStorage.getItem(
          'UserId'
        )}&Status=Pending`
      );

      if (response.data.Data?.[0]?.Id) {
        setId(response.data.Data[0].Id);
      }
    } catch (e) {
      setId(null);
    }
  };

  useEffect(() => {
    getNextDashboardId();
  }, []);

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{message}</p>
      {id && (
        <MainButton
          rounded
          variant={buttonVariants.PRIMARY}
          forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
          action={() => {
            window.location = `${window.location.origin}/dashboard/${isTimesheet ? 'timesheet' : 'expense'}/${id}`;
          }}
        >
          {isTimesheet ? t('dashboard.timesheet.nexttimesheet') : t('dashboard.expenses.nextexpense')}
        </MainButton>
      )}
      <MainButton
        rounded
        variant={buttonVariants.SECONDARY}
        outlined
        forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
        action={() => navigate(`/dashboard/${isTimesheet ? 'timesheets' : 'expenses'}${queryParams && queryParams}`)}
      >
        {t('dashboard.timesheet.gotodashboard')}
      </MainButton>
    </div>
  );
}

function ConfirmationApproveAllModal({ pendingTandECounts, confirm }) {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  return (
    <div className={classes.popupContainer}>
      <p data-testid="label" className={classes.popupLabel}>
        {t('dashboard.timesheet.approveallconfirmation')}
        {pendingTandECounts?.PendingClaimsCount > APPROVE_ALL_EXPENSES_COUNT
          ? APPROVE_ALL_EXPENSES_COUNT
          : pendingTandECounts?.PendingClaimsCount}
        {t('dashboard.timesheet.claims')}
      </p>
      <MainButton
        variant={buttonVariants.PRIMARY}
        rounded
        forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
        action={confirm}
      >
        {t('common.general.approve')}
      </MainButton>
    </div>
  );
}

function WithdrawModal({ confirm, pending }) {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.tnecommon.withdrawconfirmmsg')}</p>
      <MainButton
        variant={buttonVariants.PRIMARY}
        rounded
        forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'}
        action={confirm}
      >
        {pending ? <CircularProgress size={20} sx={{ color: colors.WHITE }} /> : t('common.general.withdraw')}
      </MainButton>
    </div>
  );
}

function AttachmentModal({ status, editeMode, isSplitApprovalFlow, timesheetId, attachments, handleChanges, sectionId }) {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  return (
    <div className="px-5 py-5 flex flex-col gap-5">
      <p className="text-navy-blue text-2xl font-medium">{t('dashboard.tnecommon.attachments')}</p>
      <Attachments
        testProp={isSplitApprovalFlow}
        status={status}
        editeMode={editeMode}
        timesheetId={timesheetId}
        attachments={attachments}
        handleChanges={handleChanges}
        sectionId={sectionId}
      />
    </div>
  );
}

function CopyModal({ timesheet }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useWindowSize();

  const firstDateofMonth = new Date(
    timesheet.TimesheetDetails[0].TimesheetHoursByDates.find((item) => new Date(item.Day).getDate() === 1)?.Day
  );
  const submissionType = (timesheet.TimesheetType.includes('Semi') || timesheet.TimesheetType.includes('Bi'))
    ? timesheet.TimesheetType.replace(/-?(Daily|Hourly)/, '').toLowerCase()
    : timesheet.TimesheetType.split('-')[0].toLowerCase();

  const isWeekly = submissionType === submissionEnum.WEEKLY;
  const isBiWeekly = submissionType === submissionEnum.BI_WEEKLY;

  const [newDate, setNewDate] = useState(
    dayjs(
      timesheet.TimesheetDetails[0].TimesheetHoursByDates[
        timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1
      ].Day
    ).add(1, 'day')
  );
  const [semiMonthDate, setSemiMonthDate] = useState(
    timesheet.TimesheetDetails[0].TimesheetHoursByDates[timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1]
      .Day
  );
  const [month, setMonth] = useState(firstDateofMonth.getMonth() + 1);
  const [year, setYear] = useState(firstDateofMonth.getFullYear());

  const onSubmit = async () => {
    const date = (isWeekly || isBiWeekly)
      ? new Date(newDate.toString())
      : getFirstDayOFMonth(new Date(year, month).toString());
    let startDate = '';

    if (isWeekly) {
      if (date.getDay() === 0) {
        startDate = dayjs(date.toString()).add(-7, 'day').day(2).toString();
      } else {
        startDate = dayjs(date.toString()).day(2).toString();
      }
    }
    let resultData = {};

    if (isWeekly) {
      const dayCount = 7;
      resultData = {
        ...timesheet,
        TimesheetDetails: timesheet.TimesheetDetails.map((ts) => ({
          ...ts,
          WorkTypeId: ts.WorkType.Id,
          TimesheetHoursByDates: Array.from({ length: dayCount }, (v, k) => k).map((increaser) => ({
            TotalHrs: '0',
            Day: dayjs(startDate)
              .add(increaser - 1, 'day')
              .format('YYYY-MM-DD')
          }))
        })),
        TimesheetPerDiem: Array.from({ length: dayCount }, (v, k) => k).map((increaser) => ({
          IsPerDiem: false,
          Date: dayjs(startDate)
            .add(increaser - 1, 'day')
            .format('YYYY-MM-DD')
        }))
      };
    } else {
      const actualDate = dayjs(date);
      const yearNew = actualDate.year();
      const monthNew = actualDate.month();

      let datesToAdd = [];

      switch (submissionType) {
        case submissionEnum.MONTHLY:
          datesToAdd = getAllDaysInMonth(yearNew, monthNew);
          break;
        case submissionEnum.BI_WEEKLY:
          datesToAdd = getBiMonthlyDays(actualDate, true);
          break;
        default:
          datesToAdd = getBiMonthlyDays(semiMonthDate);
      }

      resultData = {
        ...timesheet,
        TimesheetDetails: timesheet.TimesheetDetails.map((ts) => ({
          ...ts,
          WorkTypeId: ts.WorkType.Id,
          TimesheetHoursByDates: datesToAdd.map((day) => ({
            TotalHrs: '0',
            Day: day
          }))
        })),
        TimesheetPerDiem: datesToAdd.map((day) => ({
          IsPerDiem: false,
          Date: day
        }))
      };
    }

    try {
      const response = await tneAxiosClient.post('/v1/timesheet', resultData);
      window.location = `${window.location.origin}/dashboard/timesheet/${response.data.Data.Id}?flag=created`;
    } catch (e) {
      basicErrorHandler(dispatch, e.response.data);
    }
  };
  return (
    <div className={`${classes.containerNoPadding} ${classes.withPadding}`}>
      <p className={classes.headLabel}>
        {submissionType === submissionEnum.MONTHLY && t('dashboard.createtimesheet.steps.selectmonth')}
        {submissionType === submissionEnum.WEEKLY && t('dashboard.createtimesheet.steps.selectweek')}
        {submissionType === submissionEnum.BI_WEEKLY && t('dashboard.createtimesheet.steps.selectbimonthly')}
        {submissionType === submissionEnum.BI_MONTHLY && t('dashboard.createtimesheet.steps.selectbimonthly')}
      </p>
      {submissionType === submissionEnum.MONTHLY && (
        <CustomMonthPicker
          month={month}
          year={year}
          setMonth={setMonth}
          setYear={setYear}
          backgroundColor={colors.WHITE}
          disabledBeforeIncluded={{ month: firstDateofMonth.getMonth(), year: firstDateofMonth.getFullYear() }}
        />
      )}
      {(isWeekly) && (
        <div className={classes.calendarWrapper}>
          <CustomCalendarPicker
            userDate={newDate}
            setUserDate={(date) => setNewDate(date)}
            calendarType={calendarTypes.WEEK}
            backgroundColor={colors.WHITE}
            disabledBeforeIncluded={dayjs(
              timesheet.TimesheetDetails[0].TimesheetHoursByDates[
                timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1
              ].Day
            ).add(1, 'day')}
          />
        </div>
      )}
      {(isBiWeekly) && (
        <div className={classes.calendarWrapper}>
          <CustomBiWeeklyPicker
            userDate={newDate}
            setUserDate={(date) => setNewDate(date)}
            calendarType={calendarTypes.WEEK}
            backgroundColor={colors.WHITE}
            disabledBeforeIncluded={dayjs(
              timesheet.TimesheetDetails[0].TimesheetHoursByDates[
                timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1
              ].Day
            ).add(1, 'day')}
            showControls={false}
          />
        </div>
      )}
      {submissionType === submissionEnum.BI_MONTHLY && (
        <div className={classes.calendarWrapper}>
          <CustomBiMonthlyPicker
            userDate={semiMonthDate}
            setUserDate={(date) => setSemiMonthDate(date)}
            calendarType={calendarTypes.WEEK}
            backgroundColor={colors.WHITE}
            disabledBeforeIncluded="false"
            copyFrom="true"
          />
        </div>
      )}
      <MainButton rounded forceWidth={width > responsiveSizeTimesheet ? '244px' : '100%'} action={onSubmit}>
        {t('common.general.next')}
      </MainButton>
    </div>
  );
}

function RejectModal() {
  const { t } = useTranslation();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.timesheet.rejectmessagereq')}</p>
    </div>
  );
}

function EditPerDiemModal({ dataSet }) {
  const { data, isUpdate, submissionType, currentMonth, savePDHandler, closePDHandler, deletePDHandler } = dataSet;
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);
  const [editableData, setEditableData] = useState(data);

  const changeHandler = (index, value) => {
    if (!hasChanges) setHasChanges(true);
    setEditableData(editableData.map((pd, i) => (index === i ? { ...pd, IsPerDiem: value } : pd)));
  };

  const monthlyChangeHandler = (date, newValue) => {
    if (!hasChanges) setHasChanges(true);
    setEditableData(editableData.map((pd) => (dayjs(date).isSame(pd.Date) ? { ...pd, IsPerDiem: newValue } : pd)));
  };

  return (
    <div
      className={`${classes.containerNoPadding} ${submissionType === submissionEnum.MONTHLY ? classes.withPadding : ''
        }`}
    >
      <p className={classes.popupLabelNoMargin}>
        {isUpdate ? t('common.general.update') : t('common.general.add')} {t('dashboard.timesheet.perdiem')}
      </p>
      <div className={classes.perDiemContainer}>
        {submissionType === submissionEnum.WEEKLY ? (
          editableData.map((item, index) => (
            <div className={classes.pdItem} key={item.Date}>
              <p>{dayjs(item.Date).format('ddd')}</p>
              <p>{dayjs(item.Date).format('D')}</p>
              <OutlinedCheckBox
                value={item.IsPerDiem}
                onChange={() => changeHandler(index, !item.IsPerDiem)}
                disabled={dayjs(item.Date).month() !== currentMonth && submissionType === submissionEnum.MONTHLY}
                size="24px"
              />
            </div>
          ))
        ) : (
          <section className={classes.wrapper}>
            {shortDays.map((dayName, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={index} className={classes.dayTitle}>
                {dayName}
              </p>
            ))}
            {submissionType === submissionEnum.BI_WEEKLY ? editableData.map((item) => {
              return (
                <div key={item.Date} className={classes.pdItemMonthly}>
                  <p>{new Date(item.Date).getDate()}</p>
                  <OutlinedCheckBox
                    value={item.IsPerDiem}
                    onChange={() => monthlyChangeHandler(item.Date, !item.IsPerDiem)}
                    size="24px"
                  />
                </div>
              )
            }) : editableData.map((item) =>
              new Date(item.Date).getMonth() === currentMonth ? (
                <div key={item.Date} className={classes.pdItemMonthly}>
                  <p>{new Date(item.Date).getDate()}</p>
                  <OutlinedCheckBox
                    value={item.IsPerDiem}
                    onChange={() => monthlyChangeHandler(item.Date, !item.IsPerDiem)}
                    size="24px"
                  />
                </div>
              ) : (
                <p key={item.Date}>&nbsp;</p>
              )
            )}
          </section>
        )}
      </div>
      <div className={classes.actionContainer}>
        <MainButton
          rounded
          outlined
          forceWidth="129px"
          variant="disabled"
          action={isUpdate ? () => deletePDHandler(editableData) : closePDHandler}
        >
          {isUpdate ? t('common.general.delete') : t('common.general.cancel')}
        </MainButton>
        <MainButton
          rounded
          forceWidth="129px"
          variant={hasChanges ? 'primary' : 'disabled'}
          disabled={!hasChanges}
          action={() => savePDHandler(editableData)}
        >
          {isUpdate ? t('common.general.update') : t('common.general.save')}
        </MainButton>
      </div>
    </div>
  );
}

export {
  UnsavedChanges,
  DeletePopUp,
  FinalModal,
  ConfirmationApproveAllModal,
  WithdrawModal,
  RejectModal,
  EditPerDiemModal,
  CopyModal,
  PlacementModal,
  ApproveAllFinalModal,
  TimeandExpensePopup,
  SubmitModal,
  AttachmentModal
};
